import { FC, MouseEvent } from "react";
import { ArrowRight, ExternalLink } from "react-feather";
import clsx from "clsx";
import Link from "next/link";

import TButton from "./TButton";
import { TButtonProps } from "./TButton";
import { trackEvent } from "@/utils/analytics";

// ----------------------------------------------------------------------------
// ActionButton — companion to CTAButton.
//
// The split:
//   - CTAButton  → real conversion CTAs (Schedule a Meet, Get Consultation,
//                  Start Assessment, Subscribe). Tracks `cta_click` /
//                  `start_assessment` events that we count as funnel steps
//                  in GA4. Visual: bold (orange or blue), large, shadow on
//                  hover. Reads as "do this and convert."
//
//   - ActionButton → navigational / exploratory buttons (Explore an
//                    industry page, Read reviews on Clutch, View case
//                    study, View all services). Tracks `nav_click`, NOT a
//                    conversion. Visual: more restrained — no shadow lift,
//                    smaller default size. Reads as "go look at this."
//
// Two render modes:
//   - mode="button" (default) → solid blue or outline-brand pill button
//   - mode="link"             → text-only link with the same arrow-slide
//                               icon. Use for inline / in-flow nav.
// ----------------------------------------------------------------------------

export type ActionButtonProps = Omit<TButtonProps, "size" | "variant"> & {
  /** "button" = pill button. "link" = text-only link with arrow. */
  mode?: "button" | "link";
  /** Pill button variant. Ignored when mode="link". */
  variant?: "primary" | "outline-brand";
  size?: "sm" | "md";
  iconPosition?: "left" | "right";
  iconType?: "arrow" | "external";
  showIcon?: boolean;
};

const ActionButton: FC<ActionButtonProps> = ({
  children,
  mode = "button",
  variant = "primary",
  size = "sm",
  iconPosition = "right",
  iconType = "arrow",
  showIcon = true,
  radius = "full",
  className,
  href,
  target,
  id,
  ...props
}) => {
  const iconSize = mode === "link" ? 16 : size === "md" ? 18 : 16;
  const iconClass =
    "transition-transform duration-300 group-hover:translate-x-1";

  const IconNode = !showIcon ? null : iconType === "external" ? (
    <ExternalLink size={iconSize} className={iconClass} />
  ) : (
    <ArrowRight size={iconSize} className={iconClass} />
  );

  // Auto-track every ActionButton click as `nav_click`. Mirrors CTAButton's
  // tracking shape so we can compare CTA vs nav volume in GA4, but the
  // event name keeps the two streams cleanly separated — nav clicks are
  // NOT conversions and should NOT pollute funnel reports.
  const handleClick = (e: MouseEvent<HTMLElement>) => {
    const navText =
      typeof children === "string" ? children : (id ?? "unknown");
    trackEvent("nav_click", {
      nav_id: id || "unnamed",
      nav_text: String(navText).slice(0, 80),
      nav_href: href,
      nav_mode: mode,
    });
    (
      props as { onClick?: (e: MouseEvent<HTMLElement>) => void }
    ).onClick?.(e);
  };

  // ---- LINK MODE — text + arrow, no pill chrome ----
  if (mode === "link") {
    const content = (
      <>
        {iconPosition === "left" && IconNode}
        <span>{children}</span>
        {iconPosition === "right" && IconNode}
      </>
    );

    const linkClasses = clsx(
      // Responsive: 14px on mobile → 16px on md+. At sm the link read
      // as a footnote-style afterthought; base sits flush with body copy
      // on tablet/desktop so the nav cue is legible at a glance.
      "group inline-flex items-center gap-1.5 font-bricolage-grotesque text-sm font-medium text-primary-900 underline-offset-4 transition-all duration-300 hover:underline md:text-base",
      className,
    );

    if (href) {
      return (
        <Link
          id={id}
          href={href}
          target={target}
          className={linkClasses}
          onClick={handleClick}
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        id={id}
        type="button"
        className={linkClasses}
        onClick={handleClick}
      >
        {content}
      </button>
    );
  }

  // ---- BUTTON MODE — pill button, restrained vs CTAButton ----
  // Responsive padding + font so the button stays in proportion on
  // mobile (where the previous fixed px-7 was too wide for a 360px
  // viewport with anything longer than a one-word label).
  const sizeClasses =
    size === "md"
      ? "px-5 py-2 text-sm md:px-6 md:text-base lg:px-7"
      : "px-4 py-1.5 text-xs md:px-5 md:text-sm";

  return (
    <TButton
      id={id}
      href={href}
      target={target}
      variant={variant}
      radius={radius}
      size="base"
      className={clsx(
        // Restrained vs CTAButton: no hover:shadow-xl, no scale.
        // gap-2 (vs CTAButton's gap-4) — icon sits closer to text since
        // this isn't a "conversion" button so it doesn't need to read as
        // a big standalone action.
        // [&_.mantine-Button-label]:overflow-visible — Mantine clips its
        // inner label, which clipped the arrow's group-hover:translate-x-1.
        // Letting it overflow is fine; the outer button has its own
        // padding so the icon never escapes the visual button bounds.
        "group flex h-auto w-fit items-center justify-center gap-2 font-bricolage-grotesque font-medium transition-all duration-300 [&_.mantine-Button-label]:overflow-visible",
        sizeClasses,
        className,
      )}
      {...props}
      onClick={handleClick}
    >
      {iconPosition === "left" && IconNode}
      <span className="whitespace-nowrap">{children}</span>
      {iconPosition === "right" && IconNode}
    </TButton>
  );
};

export default ActionButton;
